import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const { refresh } = useAuth();
  const ran = useRef(false);
  usePageTitle("Verify your email — Olivia Does");

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setStatus("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setStatus("success");
        setMessage(data.message || "Your email is verified.");
        refresh();
      } catch (err) {
        setStatus("error");
        setMessage(formatApiError(err.response?.data?.detail));
      }
    })();
  }, [token, refresh]);

  return (
    <div data-testid="verify-email-page" className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md od-card p-8 text-center">
        {status === "verifying" && (
          <div data-testid="verify-loading">
            <Loader2 className="w-10 h-10 text-[#C97C8C] mx-auto animate-spin" />
            <h1 className="font-display text-2xl mt-5">Verifying your email…</h1>
          </div>
        )}
        {status === "success" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="w-12 h-12 text-[#4a7c59] mx-auto" />
            <h1 className="font-display text-3xl mt-5">You're all set</h1>
            <p className="text-sm text-[#78716C] mt-2">{message}</p>
            <Link to="/dashboard" className="inline-block mt-6 rounded-full bg-[#2A2624] hover:bg-[#1E1A18] text-white px-8 py-3 text-sm">Go to dashboard</Link>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-error">
            <XCircle className="w-12 h-12 text-[#993333] mx-auto" />
            <h1 className="font-display text-3xl mt-5">Link expired or invalid</h1>
            <p className="text-sm text-[#78716C] mt-2">{message}</p>
            <Link to="/dashboard" className="inline-block mt-6 od-link font-medium text-sm">Go to dashboard to resend</Link>
          </div>
        )}
      </div>
    </div>
  );
}
